import { ActivityVariableDefinition } from '../types/activity';

// Unit options for different variable types
export const UNIT_OPTIONS: Record<string, string[]> = {
  // Distance units
  Distance: ['km', 'mi', 'm', 'yd', 'ft'],
  
  // Time units
  Time: ['min', 'sec', 'hr'],
  
  // Weight units
  Weight: ['kg', 'lbs', 'g'],
  
  // Speed units
  Speed: ['km/h', 'mph', 'm/s', 'min/km', 'min/mi', '%'],
  
  // Repetition units
  Reps: ['reps', 'count', 'number'],
  
  // Intensity units
  Intensity: ['%', 'RPE', 'level'],
  
  // Incline units
  Incline: ['%', 'degrees'],
  
  // Rest units
  Rest: ['sec', 'min'],
  
  // Side units (for unilateral exercises)
  Side: ['both', 'left', 'right'],

  // Default for custom variables
  Default: ['units', 'count', '%'],
};

// Default units for preset variables (metric)
export const DEFAULT_UNITS_METRIC: Record<string, string> = {
  Distance: 'km',
  Time: 'min',
  Weight: 'kg',
  Speed: 'km/h',
  Reps: 'reps',
  Intensity: '%',
  Incline: '%',
  Rest: 'sec',
  Side: 'both',
};

// Default units for preset variables (imperial)
export const DEFAULT_UNITS_IMPERIAL: Record<string, string> = {
  Distance: 'mi',
  Time: 'min',
  Weight: 'lbs',
  Speed: 'mph',
  Reps: 'reps',
  Intensity: '%',
  Incline: '%',
  Rest: 'sec',
  Side: 'both',
};

// Legacy export for backwards compatibility
export const DEFAULT_UNITS = DEFAULT_UNITS_METRIC;

/**
 * Get available units for a variable name
 * @param variableName - The name of the variable
 * @param isImperial - If true, reorder units to put imperial units first
 */
export function getUnitsForVariable(variableName: string, isImperial?: boolean): string[] {
  // Check if we have specific units for this variable
  let units = UNIT_OPTIONS[variableName] ? [...UNIT_OPTIONS[variableName]] : [...UNIT_OPTIONS.Default];
  
  // Reorder units based on measurement system preference
  if (isImperial) {
    // Define imperial unit priorities
    const imperialPriority: Record<string, string[]> = {
      Distance: ['mi', 'yd', 'ft', 'km', 'm'],
      Weight: ['lbs', 'kg', 'g'],
      Speed: ['mph', 'min/mi', 'km/h', 'min/km', 'm/s'],
    };
    
    if (imperialPriority[variableName]) {
      // Sort units to put imperial first
      units.sort((a, b) => {
        const aIndex = imperialPriority[variableName].indexOf(a);
        const bIndex = imperialPriority[variableName].indexOf(b);
        // If both are in priority list, sort by priority
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        // If only a is in priority list, it comes first
        if (aIndex !== -1) return -1;
        // If only b is in priority list, it comes first
        if (bIndex !== -1) return 1;
        // Otherwise keep original order
        return 0;
      });
    }
  }
  
  return units;
}

/**
 * Get default unit for a variable name
 * @param variableName - The name of the variable
 * @param isImperial - If true, return imperial default unit
 */
export function getDefaultUnit(variableName: string, isImperial?: boolean): string {
  // Check if we have a default for this variable
  const defaults = isImperial ? DEFAULT_UNITS_IMPERIAL : DEFAULT_UNITS_METRIC;
  if (defaults[variableName]) {
    return defaults[variableName];
  }
  
  // For custom variables, use the first available option
  const units = getUnitsForVariable(variableName, isImperial);
  return units[0];
}

/**
 * Convert legacy string variable to ActivityVariableDefinition
 */
export function convertLegacyVariable(variable: string | ActivityVariableDefinition): ActivityVariableDefinition {
  if (typeof variable === 'string') {
    // Legacy format - convert to new format with default unit
    return {
      name: variable,
      defaultUnit: getDefaultUnit(variable),
    };
  }
  // Already in new format
  return variable;
}

/**
 * Normalize activity type variables to new format
 * Handles both legacy (string[]) and new (ActivityVariableDefinition[]) formats
 */
export function normalizeActivityVariables(
  variables: (string | ActivityVariableDefinition)[]
): ActivityVariableDefinition[] {
  return variables.map(convertLegacyVariable);
}

/**
 * Get variable definition by name from a variables array
 */
export function getVariableDefinition(
  variables: (string | ActivityVariableDefinition)[],
  variableName: string
): ActivityVariableDefinition | undefined {
  const normalized = normalizeActivityVariables(variables);
  return normalized.find(v => v.name === variableName);
}

/**
 * Format measurement with unit for display
 */
export function formatMeasurementWithUnit(
  measurement: number | null,
  unit?: string
): string {
  if (measurement === null || measurement === undefined) {
    return '-';
  }
  
  if (!unit) {
    return measurement.toString();
  }
  
  // Format number with appropriate decimal places
  const formatted = measurement % 1 === 0 
    ? measurement.toString() 
    : measurement.toFixed(2);
  
  return `${formatted} ${unit}`;
}

